import { useCallback } from "react";
import apiClient from "../../api/axios";
import { FlowState } from "./types";
import { ExtractedContract } from "../../types/contract";

export const useDetailedExtraction = (
  companyId: string | undefined,
  generalContracts: ExtractedContract[],
  setError: (error: string | null) => void,
  setFlowState: (state: FlowState) => void,
  setLoading: (loading: boolean) => void,
  setFinalContracts: (updater: (prev: ExtractedContract[]) => ExtractedContract[]) => void,
  setDetailedJobIds: (jobIds: string[]) => void,
  setDetailedJobStatuses: (statuses: Record<string, { status: "processing" | "error" | "complete" }>) => void
) => {
  const handleExtractDetails = useCallback(async () => {
    if (!companyId) {
      setError("ログイン情報が確認できません。再度ログインしてください。");
      setFlowState("error");
      return;
    }

    // 全般データの必須フィールドの検証
    const emptyFields: string[] = [];
    const emptyContracts: Array<{ index: number; filename: string; fields: string[] }> = [];

    generalContracts.forEach((contract, index) => {
      const contractEmptyFields: string[] = [];
      
      if (!contract.property_name || contract.property_name.trim() === '') {
        contractEmptyFields.push('物件名');
      }
      if (!contract.room_name || contract.room_name.trim() === '') {
        contractEmptyFields.push('部屋名');
      }
      if (!contract.lessee_name || contract.lessee_name.trim() === '') {
        contractEmptyFields.push('借主名');
      }

      if (contractEmptyFields.length > 0) {
        emptyContracts.push({
          index: index + 1,
          filename: contract.filename,
          fields: contractEmptyFields
        });
      }
    });

    if (emptyContracts.length > 0) {
      const errorMessages = emptyContracts.map(contract => 
        `${contract.index}番目のファイル「${contract.filename}」: ${contract.fields.join('、')}が空白です`
      );
      setError(`詳細抽出を実行する前に、以下の必須項目を入力してください：\n\n${errorMessages.join('\n')}`);
      return;
    }

    setLoading(true);
    setError(null);

    // Only remove finalContracts that correspond to generalContracts being re-extracted
    // This preserves any edited data from contracts not being re-processed
    const generalContractKeys = new Set(
      generalContracts.map(gc => `${gc.property_name}|${gc.room_name}|${gc.lessee_name}`)
    );
    setFinalContracts(prev =>
      prev.filter(fc =>
        !generalContractKeys.has(`${fc.property_name}|${fc.room_name}|${fc.lessee_name}`)
      )
    );

    try {
      // Remove id field before sending to detailed extraction (id is for tmpDB deletion only)
      const contractsForExtraction = generalContracts.map(({ id, ...rest }) => rest);

      const response = await apiClient.post(
        `/api/extract-detailed-data/`,
        {
          contracts: contractsForExtraction,
        }
      );
      const { job_ids } = response.data;
      setDetailedJobIds(job_ids);
      const initialStatuses = job_ids.reduce(
        (acc: Record<string, { status: "processing" | "error" | "complete" }>, jobId: string) => {
          acc[jobId] = { status: "processing" };
          return acc;
        },
        {}
      );
      setDetailedJobStatuses(initialStatuses);
      setFlowState("detailedProcessing");
    } catch (err) {
      setError("詳細データの抽出中にエラーが発生しました。");
      setFlowState("error");
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    generalContracts,
    setError,
    setFlowState,
    setLoading,
    setFinalContracts,
    setDetailedJobIds,
    setDetailedJobStatuses,
  ]);

  return {
    handleExtractDetails,
  };
};